import React from 'react'
import {FormattedDate} from 'react-intl'
import {useSelector} from 'react-redux'
import {cx} from '@emotion/css'
import styled from '@emotion/styled'
import {Box} from '@vio/atlas/helpers/Box'
import {Image} from '@vio/atlas/Image'
import {Text} from '@vio/atlas/Text'
import {cssTheme} from '@vio/atlas/themes'
import {HOME_PAGE_HOTEL_CARD_WIDTH} from '@vio/shared/src/search/constants'
import {GuestAndRoomText} from '@vio/shared/src/search/GuestAndRoomsText'
import {OfferPrice} from 'components/Offer/Shared/OfferPrice'
import {getShowTotalPrices} from 'modules/sapiSearch/selectors'
import {SearchOffer} from 'modules/sapiSearch/slice'
import {imageProvider} from 'utils/imageProvider'

import FormattedMessageWrapper from '@daedalus/core/src/localization/components/FormattedMessage'
import {dateStringToMiddayDate} from '@daedalus/core/src/utils/date'

const Wrapper = styled.div`
  display: flex;
  width: ${HOME_PAGE_HOTEL_CARD_WIDTH};
  background-color: ${cssTheme.colors.background.neutral.c000};
  border: 1px solid ${cssTheme.colors.border.neutral.c100};
  border-radius: ${cssTheme.layout.radius.lg};
  overflow: hidden;
  flex: 0 0 auto;
  align-items: stretch;

  &.__isFullWidth {
    width: 100%;
    flex: 1 0 auto;
  }

  &.__hasOnClick {
    cursor: pointer;
  }
`

const HotelInfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  overflow: hidden;
  padding: ${cssTheme.layout.spacing.s300} ${cssTheme.layout.spacing.s400}
    ${cssTheme.layout.spacing.s250} ${cssTheme.layout.spacing.s400};
`

const HotelName = styled(Text)`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  width: 144px;

  &.__fullWidth {
    width: 100%;
  }
`

const BodyText = styled(Text)`
  color: ${cssTheme.colors.content.neutral.c600};
`

const ImageContainer = styled.div`
  min-width: 56px;
  min-height: 88px;

  &.__isCompactImage {
    min-height: 64px;
  }
`

const PriceWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-width: fit-content;
  text-transform: lowercase;
  padding: ${cssTheme.layout.spacing.s400};
`
const Spacer = styled.div`
  flex: 1;
`

interface Props {
  imageUrl: string
  title: string
  checkIn?: string
  checkOut?: string
  rooms: string
  placeholderImage: string
  onClick?: (event: React.MouseEvent<HTMLDivElement>) => void
  fullWidth?: boolean
  testId?: string
  isCompactImage?: boolean
  hotelId?: string
  offer?: SearchOffer
}

export const MinimalHotelCard: React.FC<Props> = ({
  imageUrl,
  title,
  checkIn,
  checkOut,
  rooms,
  placeholderImage,
  onClick,
  fullWidth,
  testId,
  isCompactImage = false,
  offer,
  hotelId
}) => {
  const showTotalPrices = useSelector(getShowTotalPrices)
  const shouldShowOffer = offer && hotelId

  return (
    <Wrapper
      data-id={testId}
      className={cx(fullWidth && '__isFullWidth', onClick && '__hasOnClick')}
      onClick={onClick}
    >
      <ImageContainer className={cx(isCompactImage && '__isCompactImage')}>
        <Image
          url={imageUrl}
          placeHolderImage={placeholderImage}
          imageProvider={imageProvider}
          size="main"
          isResponsive
          hasSkeletonLoader
        />
      </ImageContainer>
      <HotelInfoContainer>
        <HotelName variant="labelS" className={cx(fullWidth && '__fullWidth')}>
          {title}
        </HotelName>
        <div>
          {checkIn && checkOut && (
            <Box display="flex" alignItems="center" gap="s200">
              <BodyText variant="bodyS">
                <FormattedDate
                  value={dateStringToMiddayDate(checkIn)}
                  month="short"
                  day="2-digit"
                />
              </BodyText>
              -
              <BodyText variant="bodyS">
                <FormattedDate
                  value={dateStringToMiddayDate(checkOut)}
                  month="short"
                  day="2-digit"
                />
              </BodyText>
            </Box>
          )}
          <BodyText variant="bodyS">
            <GuestAndRoomText rooms={rooms} />
          </BodyText>
        </div>
      </HotelInfoContainer>
      {shouldShowOffer && (
        <>
          <Spacer />
          <PriceWrapper>
            <OfferPrice
              size="lg"
              offer={offer}
              hotelId={hotelId}
              anchorPrice={undefined}
            />
            <Text variant="bodyS" colorPath="content.neutral.c600" align="end">
              {showTotalPrices ? (
                <FormattedMessageWrapper
                  id="payment.total"
                  defaultMessage="total"
                />
              ) : (
                <FormattedMessageWrapper
                  id="perNight"
                  defaultMessage="/ night"
                />
              )}
            </Text>
          </PriceWrapper>
        </>
      )}
    </Wrapper>
  )
}
